import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Users, Calendar, MapPin, Filter, AlertTriangle, Clock, ChevronRight } from 'lucide-react';

const SESSION_TYPE_LABELS = {
  group_support: 'Group Support',
  community_access: 'Community Access',
  centre_based: 'Centre-Based',
  day_program: 'Day Program',
  social_recreation: 'Social & Recreation',
  transport: 'Transport',
  other: 'Other',
};

const LOCATION_TYPE_LABELS = {
  centre: 'Centre',
  house: 'House',
  community: 'Community',
  other: 'Other',
};

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  published: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

const STATUS_LABELS = {
  draft: 'Draft',
  published: 'Published',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export default function GroupSessionsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Filters
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [filterType, setFilterType] = useState('__all__');

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  // Enums
  const [enums, setEnums] = useState({});

  // States/branches
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);

  // Ratio rules for dropdown
  const [ratioRules, setRatioRules] = useState([]);

  // Feature flag
  const [featureEnabled, setFeatureEnabled] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: '',
    session_type: 'group_support',
    location_type: 'centre',
    location_id: '',
    start_datetime: '',
    end_datetime: '',
    ratio_rule_id: '',
    required_staff_count: 1,
    billing_mode: 'per_participant',
    funding_mode: 'individual',
    notes: '',
    state_id: '',
    branch_id: '',
  });

  useEffect(() => {
    const loadRef = async () => {
      const [enumsRes, statesRes, branchesRes, rulesRes, flagsRes] = await Promise.allSettled([
        api.get('/group-sessions/enums'),
        api.get('/org/states'),
        api.get('/org/branches'),
        api.get('/ratio-rules?active_only=true&page_size=100'),
        api.get('/production/feature-flags'),
      ]);
      if (enumsRes.status === 'fulfilled') setEnums(enumsRes.value.data.data || {});
      if (statesRes.status === 'fulfilled') setStates(statesRes.value.data.data || []);
      if (branchesRes.status === 'fulfilled') setBranches(branchesRes.value.data.data || []);
      if (rulesRes.status === 'fulfilled') setRatioRules(rulesRes.value.data.data?.items || []);
      if (flagsRes.status === 'fulfilled') {
        const flags = flagsRes.value.data?.data || {};
        setFeatureEnabled(flags.group_session_enabled?.enabled || false);
      }
    };
    loadRef();
  }, []);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (filterStatus && filterStatus !== '__all__') params.append('status', filterStatus);
      if (filterType && filterType !== '__all__') params.append('session_type', filterType);
      const res = await api.get(`/group-sessions?${params.toString()}`);
      setSessions(res.data.data.items || []);
      setTotal(res.data.data.total || 0);
    } catch {
      toast.error('Failed to load group sessions');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterStatus, filterType]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...form,
        required_staff_count: parseInt(form.required_staff_count),
        location_id: form.location_id || null,
        ratio_rule_id: form.ratio_rule_id || null,
        state_id: form.state_id || null,
        branch_id: form.branch_id || null,
      };
      const res = await api.post('/group-sessions', payload);
      toast.success('Group session created as draft');
      setShowCreate(false);
      resetForm();
      navigate(`/admin/group-sessions/${res.data.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail?.error?.message || 'Failed to create session';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: '', session_type: 'group_support', location_type: 'centre', location_id: '',
      start_datetime: '', end_datetime: '', ratio_rule_id: '', required_staff_count: 1,
      billing_mode: 'per_participant', funding_mode: 'individual', notes: '', state_id: '', branch_id: '',
    });
  };

  if (!isAdmin) {
    return (
      <div className="p-8"><Card><CardContent className="py-12 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
        <p className="text-lg font-medium">Access Denied</p>
        <p className="text-muted-foreground">You need admin access to manage group sessions.</p>
      </CardContent></Card></div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="group-sessions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" /> Group Sessions
          </h1>
          <p className="text-muted-foreground mt-1">Create and manage draft group sessions with linked participants and staff</p>
        </div>
        <div className="flex items-center gap-3">
          {!featureEnabled && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              <AlertTriangle className="h-3 w-3 mr-1" /> Feature Disabled
            </Badge>
          )}
          <Button onClick={() => { resetForm(); setShowCreate(true); }} data-testid="create-group-session-btn">
            <Plus className="h-4 w-4 mr-2" /> New Session
          </Button>
        </div>
      </div>

      {!featureEnabled && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <p className="font-medium text-orange-800">Group Sessions Feature is Disabled</p>
                <p className="text-sm text-orange-700 mt-1">
                  You can create draft sessions, but they won't affect any live workflows until enabled via <strong>Admin &rarr; Feature Flags</strong>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Filter className="h-4 w-4" /> Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Statuses</SelectItem>
                  {(enums.session_statuses || ['draft','published','in_progress','completed','cancelled']).map(s => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Session Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-session-type"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Types</SelectItem>
                  {(enums.session_types || Object.keys(SESSION_TYPE_LABELS)).map(t => (
                    <SelectItem key={t} value={t}>{SESSION_TYPE_LABELS[t] || t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => { setFilterStatus('__all__'); setFilterType('__all__'); }} data-testid="clear-filters-btn">Clear Filters</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sessions Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{total} Group Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : sessions.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No group sessions found</p>
              <p className="text-sm mt-1">Create your first group session to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map(s => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/admin/group-sessions/${s.id}`)} data-testid={`session-row-${s.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{s.title}</p>
                        <p className="text-xs text-muted-foreground font-mono">{s.session_code}</p>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{SESSION_TYPE_LABELS[s.session_type] || s.session_type}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{s.start_datetime ? new Date(s.start_datetime).toLocaleDateString() : '—'}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {s.start_datetime ? new Date(s.start_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''} — {s.end_datetime ? new Date(s.end_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        {LOCATION_TYPE_LABELS[s.location_type] || s.location_type}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`font-mono text-sm ${s.assigned_staff_count < s.required_staff_count ? 'text-orange-600' : 'text-emerald-600'}`}>
                        {s.assigned_staff_count}/{s.required_staff_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLES[s.status] || 'bg-slate-100'}>{STATUS_LABELS[s.status] || s.status}</Badge>
                    </TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > pageSize && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / pageSize)}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Create Group Session</DialogTitle>
              <DialogDescription>Sessions are created as drafts. You can add participants and staff after creation.</DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="title">Session Title *</Label>
                <Input id="title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Monday Morning Group — Community Access" required data-testid="input-title" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Session Type *</Label>
                  <Select value={form.session_type} onValueChange={v => setForm({...form, session_type: v})}>
                    <SelectTrigger data-testid="input-session-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(enums.session_types || Object.keys(SESSION_TYPE_LABELS)).map(t => (
                        <SelectItem key={t} value={t}>{SESSION_TYPE_LABELS[t] || t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location Type *</Label>
                  <Select value={form.location_type} onValueChange={v => setForm({...form, location_type: v})}>
                    <SelectTrigger data-testid="input-location-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(enums.location_types || Object.keys(LOCATION_TYPE_LABELS)).map(t => (
                        <SelectItem key={t} value={t}>{LOCATION_TYPE_LABELS[t] || t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date & Time *</Label>
                  <Input type="datetime-local" value={form.start_datetime} onChange={e => setForm({...form, start_datetime: e.target.value})} required data-testid="input-start-datetime" />
                </div>
                <div>
                  <Label>End Date & Time *</Label>
                  <Input type="datetime-local" value={form.end_datetime} onChange={e => setForm({...form, end_datetime: e.target.value})} required data-testid="input-end-datetime" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Required Staff Count *</Label>
                  <Input type="number" min="1" max="20" value={form.required_staff_count} onChange={e => setForm({...form, required_staff_count: e.target.value})} required data-testid="input-required-staff" />
                </div>
                <div>
                  <Label>Ratio Rule</Label>
                  <Select value={form.ratio_rule_id || '__none__'} onValueChange={v => setForm({...form, ratio_rule_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger data-testid="input-ratio-rule"><SelectValue placeholder="None (manual)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None (manual)</SelectItem>
                      {ratioRules.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.name} (1:{r.participants_per_staff})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Billing Mode</Label>
                  <Select value={form.billing_mode} onValueChange={v => setForm({...form, billing_mode: v})}>
                    <SelectTrigger data-testid="input-billing-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_participant">Per Participant</SelectItem>
                      <SelectItem value="group_flat">Group Flat Rate</SelectItem>
                      <SelectItem value="ratio_based">Ratio-Based</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Funding Mode</Label>
                  <Select value={form.funding_mode} onValueChange={v => setForm({...form, funding_mode: v})}>
                    <SelectTrigger data-testid="input-funding-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>State (Scope)</Label>
                  <Select value={form.state_id || '__auto__'} onValueChange={v => setForm({...form, state_id: v === '__auto__' ? '' : v})}>
                    <SelectTrigger data-testid="input-state"><SelectValue placeholder="Auto (your state)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__">Auto (your state)</SelectItem>
                      {states.map(s => <SelectItem key={s.state_id || s.id} value={s.state_id || s.id}>{s.name || s.state_id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Branch (Scope)</Label>
                  <Select value={form.branch_id || '__auto__'} onValueChange={v => setForm({...form, branch_id: v === '__auto__' ? '' : v})}>
                    <SelectTrigger data-testid="input-branch"><SelectValue placeholder="Auto (your branch)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__">Auto (your branch)</SelectItem>
                      {branches.map(b => <SelectItem key={b.branch_id || b.id} value={b.branch_id || b.id}>{b.name || b.branch_id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes for this session..." rows={3} data-testid="input-notes" />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="submit-create-session">
                {creating ? 'Creating...' : 'Create Draft Session'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
